// Phase 50.2 + 50.3 — Google Maps URL helpers (frontend, pure functions).
//
// Two responsibilities:
//   1. parseMapsUrl(text)  → detect a Maps URL pasted into a location
//      input and extract { url, address?, lat?, lng? }. Pure regex; no
//      network calls. Recognises the common public formats:
//        - short links: maps.app.goo.gl/..., goo.gl/maps/...
//        - place URLs: google.com/maps/place/{address}/@lat,lng,zoom
//        - coord URLs: google.com/maps/?q=lat,lng | ?ll=lat,lng | @lat,lng
//        - search URLs: google.com/maps/search/?api=1&query={addr-or-coords}
//
//   2. buildMapsUrl(loc) → produce the best "Open in Maps" URL for a
//      booking location. Priority:
//        a) loc.location_url (manager-supplied verbatim — never rebuild)
//        b) loc.lat && loc.lng (coords are 100% reliable)
//        c) address_line + postcode/eircode joined + URL-encoded against
//           the modern /maps/search/?api=1&query=... endpoint.
//
// The 50.3 "no results" bug was StaffPortal building the search query
// from raw address text even when good lat/lng existed. Use buildMapsUrl
// everywhere instead of duplicating the logic.

const MAPS_HOSTS = [
  "maps.app.goo.gl",
  "goo.gl",
  "google.com",
  "www.google.com",
  "maps.google.com",
];

export function isMapsUrl(text) {
  if (!text || typeof text !== "string") return false;
  const t = text.trim();
  if (!/^https?:\/\//i.test(t)) return false;
  try {
    const u = new URL(t);
    const host = u.hostname.toLowerCase();
    if (!MAPS_HOSTS.includes(host)) return false;
    // goo.gl is also used for non-Maps short links; only accept its
    // /maps/ path. Everything else is fine.
    if (host === "goo.gl" && !u.pathname.startsWith("/maps")) return false;
    return true;
  } catch {
    return false;
  }
}

// Find a "lat,lng" pair anywhere in a string. Lat ∈ [-90,90], lng ∈ [-180,180].
const COORD_PAIR_RE = /(-?\d{1,2}(?:\.\d+)?),\s*(-?\d{1,3}(?:\.\d+)?)/;

function _validCoord(lat, lng) {
  const a = Number(lat), b = Number(lng);
  return Number.isFinite(a) && Number.isFinite(b)
    && a >= -90 && a <= 90 && b >= -180 && b <= 180;
}

/**
 * Inspect a pasted string. If it's a Google Maps URL, return
 * { url, address?, lat?, lng? }. Otherwise return null.
 */
export function parseMapsUrl(text) {
  if (!isMapsUrl(text)) return null;
  const url = text.trim();
  const out = { url };
  let u;
  try { u = new URL(url); } catch { return out; }

  // 1) /maps/place/{address}/@lat,lng,zoom  (most common public share)
  const placeMatch = u.pathname.match(/\/maps\/place\/([^/@]+)(?:\/@(-?\d+\.?\d*),(-?\d+\.?\d*))?/i);
  if (placeMatch) {
    const rawAddr = placeMatch[1];
    try {
      const decoded = decodeURIComponent(rawAddr.replace(/\+/g, " "));
      // Maps replaces commas + spaces with `+` and `,` is URL-safe so we
      // also normalise the address-line by softening `+` separators that
      // sometimes leak through.
      out.address = decoded.replace(/\s+/g, " ").trim();
    } catch { /* leave undefined */ }
    if (placeMatch[2] && placeMatch[3] && _validCoord(placeMatch[2], placeMatch[3])) {
      out.lat = Number(placeMatch[2]);
      out.lng = Number(placeMatch[3]);
    }
  }

  // 2) bare /@lat,lng on the path
  if (out.lat === undefined) {
    const atMatch = u.pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (atMatch && _validCoord(atMatch[1], atMatch[2])) {
      out.lat = Number(atMatch[1]);
      out.lng = Number(atMatch[2]);
    }
  }

  // 3) ?q=, ?ll=, ?query= — may carry coords OR an address.
  const qStr = u.searchParams.get("q") || u.searchParams.get("ll") || u.searchParams.get("query") || "";
  if (qStr) {
    const m = qStr.match(COORD_PAIR_RE);
    if (m && _validCoord(m[1], m[2])) {
      if (out.lat === undefined) {
        out.lat = Number(m[1]);
        out.lng = Number(m[2]);
      }
    } else if (!out.address) {
      out.address = qStr.replace(/\+/g, " ").trim();
    }
  }

  return out;
}

const MAPS_SEARCH = "https://www.google.com/maps/search/?api=1&query=";

/**
 * Build the best "Open in Maps" link for a booking location.
 * @param {object} loc — { location_url?, address_line?, postcode?, eircode?, lat?, lng? }
 * @returns {string|null} URL or null if there's nothing to open.
 */
export function buildMapsUrl(loc) {
  if (!loc) return null;
  // 1) verbatim manager-supplied URL beats everything.
  if (loc.location_url && typeof loc.location_url === "string" && /^https?:\/\//i.test(loc.location_url.trim())) {
    return loc.location_url.trim();
  }
  // 2) Coords are 100% reliable. Use them whenever present.
  if (Number.isFinite(loc.lat) && Number.isFinite(loc.lng)) {
    return `${MAPS_SEARCH}${encodeURIComponent(`${loc.lat},${loc.lng}`)}`;
  }
  // 3) Fall back to a clean joined address. Trim each part, drop empties,
  //    let encodeURIComponent handle commas/em-dashes/accents/etc.
  const parts = [loc.address_line, loc.postcode || loc.eircode]
    .map((s) => (s == null ? "" : String(s).trim()))
    .filter(Boolean);
  if (parts.length === 0) return null;
  return `${MAPS_SEARCH}${encodeURIComponent(parts.join(", "))}`;
}
